/**
 * Dou Dizhu tables — all numbers of the game. No logic here.
 * Money (spec, section 3): pairwise transfers landlord ↔ each peasant, only between humans;
 * t = base × min(bid × 2^(bombs + rocket + spring), capX); fee = feeBps of each winner's gain.
 * The numbers we choose avoid the digit 4 (tiers, balances, cap, fee, timings). Bot weights (`bidCuts`) are internal.
 */
export type DdzTierId = 'novice' | 'junior' | 'middle' | 'senior';

export interface DdzTier {
  readonly id: DdzTierId;
  /** 底分, tokens */
  readonly base: number;
  /** balance needed to sit down; ≥ ddzMaxLoss(base) */
  readonly minBalance: number;
}

export interface DdzConfig {
  readonly handSize: number;
  readonly bottomSize: number;
  /** deals of one hand before the first bidder is made landlord with bid 1 */
  readonly maxDeals: number;
  /** cap of the multiple per landlord–peasant pair (封顶) */
  readonly capX: number;
  /** house fee, basis points of each winner's gain */
  readonly feeBps: number;
  readonly tiers: readonly DdzTier[];
  /** turn timers, ms (server) */
  readonly bidMs: number;
  readonly playMs: number;
  /** timeouts in a row that switch a seat to 托管 */
  readonly autoAfterTimeouts: number;
  /** delay of a 托管 move, ms */
  readonly autoPlayMs: number;
  /** quick match: bots fill empty seats after this wait, ms */
  readonly botWaitMs: number;
  /** wait for the players' salts before dealing, ms */
  readonly saltWaitMs: number;
  /** result screen before the next hand, ms */
  readonly nextHandMs: number;
  /** cosmetic bot «thinking» pause range, ms (server, outside the fair stream) */
  readonly botThinkMs: readonly [number, number];
  /** bot: hand strength needed for bid 1 / 2 / 3 */
  readonly bidCuts: readonly [number, number, number];
}

export const DDZ_CONFIG: DdzConfig = {
  handSize: 17,
  bottomSize: 3,
  maxDeals: 3,
  capX: 30,
  feeBps: 500,
  tiers: [
    { id: 'novice', base: 1, minBalance: 100 },
    { id: 'junior', base: 5, minBalance: 500 },
    { id: 'middle', base: 20, minBalance: 2_000 },
    { id: 'senior', base: 100, minBalance: 8_000 },
  ],
  bidMs: 15_000,
  playMs: 20_000,
  autoAfterTimeouts: 2,
  autoPlayMs: 1_000,
  botWaitMs: 12_000,
  saltWaitMs: 3_000,
  nextHandMs: 10_000,
  botThinkMs: [800, 1_800],
  bidCuts: [5, 8, 11],
};

export function ddzTier(id: DdzTierId, cfg: DdzConfig = DDZ_CONFIG): DdzTier {
  const t = cfg.tiers.find((x) => x.id === id);
  if (!t) throw new RangeError(`unknown tier ${id}`);
  return t;
}

/** the most a landlord facing two humans can lose in one hand */
export const ddzMaxLoss = (base: number, cfg: Pick<DdzConfig, 'capX'> = DDZ_CONFIG): number =>
  2 * cfg.capX * base;
